import { RouteKeys } from 'types/routes'
import { Language } from 'types/language'
import { IconName, ButtonProps } from '@boltenergy-be/design-system'

export type PossibleQuestions = DynamicPriceQuizQuestions
export type PossibleResults = DynamicPriceQuizResults

export type QuizProps<Q extends PossibleQuestions, R extends PossibleResults> = {
  defaultResultCard: R
  quizFlow: StackingCardsFlow<Q, R>
  resultCards: StackingResultCards<R>
  namespace: string
}

export type QuizActionButtonProps = {
  namespace: string
  actionCard: ResultCardAction
  variant?: ButtonProps['variant']
  className?: string
}

export type ResultCardAction =
  | {
      action: () => void
      externalUrl?: never
      internalRoute?: never
    }
  | {
      externalUrl: (language: Language) => string
      action?: never
      internalRoute?: never
    }
  | {
      internalRoute: RouteKeys // Route from routes util
      action?: never
      externalUrl?: never
    }

export type LinkResultCardAction = Extract<ResultCardAction, { externalUrl: unknown } | { internalRoute: unknown }>

export type StackingResultCards<R extends PossibleResults> = {
  [result in R]: {
    rating?: {
      state: 'positive' | 'informative' | 'negative' | 'warning'
    }
    actions: {
      primary: ResultCardAction
      secondary?: ResultCardAction
    }
  }
}

export type QuestionValue = boolean | number

export type StackingCardFlowAction<Q extends PossibleQuestions, R extends PossibleResults> = {
  next: (value: FlowUserInputs<Q>) => Q | R
  icon: IconName
  value: QuestionValue
}

export type StackingCardsFlow<Q extends PossibleQuestions, R extends PossibleResults> = {
  question: Q
  actions: {
    yes: StackingCardFlowAction<Q, R>
    no: StackingCardFlowAction<Q, R>
    maybe?: StackingCardFlowAction<Q, R>
  }
}[]

export type FlowUserInputs<Q extends PossibleQuestions> = {
  [question in Q]?: QuestionValue
}

// Enums

export enum DynamicPriceQuizQuestions {
  LOCATION = 'location',
  METER = 'meter',
  SOLAR_PANELS_OR_HOME_BATTERY = 'solarPanelsOrHomeBattery',
  ELECTRIC_VEHICLE = 'electricVehicle',
  SMART_HOME = 'smartHome'
}

export enum DynamicPriceQuizResults {
  UNAVAILABLE_FLANDERS_ONLY = 'unavailableFlandersOnly',
  UNAVAILABLE_DIGITAL_METER_REQUIRED = 'unavailableDigitalMeterRequired',
  UNAVAILABLE_GENERAL = 'unavailableGeneral',
  INTERESTING = 'interesting',
  CONSIDERABLE = 'considerable',
  NOT_INTERESTING = 'notInteresting'
}
